import React, { useEffect, useRef, useState } from 'react'
import { View, Animated } from 'react-native'
import Swipeable from 'react-native-gesture-handler/Swipeable'
import { useDispatch, useSelector } from 'react-redux'
import { setQuotedText, setActiveChatMessageId } from '../../../../redux/actions'
import { colors } from '../../../styles/global'
import SwipeAreasContainer from '../../../TaskListView/SwipeAreasContainer'

import MessageItemContent from './MessageItemContent'
import MessageItemHeader from './MessageItemHeader'
import useGetUserPresentationData from '../../../ContactsView/Utils/useGetUserPresentationData'
import { getTimestampInMilliseconds } from '../../Utils/ChatHelper'
import SharedHelper from '../../../../utils/SharedHelper'
import { resolveEffectiveMessageLoading } from './messageLoadingState'
import useSwipeCloseGuard from '../../../../hooks/useSwipeCloseGuard'

export const QUEUED_CONTENT_STYLE = { opacity: 0.6 }

export default function MessageItem({
    chat,
    projectId,
    message,
    serverTime,
    chatTitle,
    members,
    objectType,
    highlight,
    linkedEmail,
    linkedEmailNew,
    linkedEmailArchiving,
    linkedEmailArchived,
    onArchiveLinkedEmail,
    setAmountOfNewCommentsToHighligth,
}) {
    const dispatch = useDispatch()
    const userIsAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const loggedUser = useSelector(state => state.loggedUser)
    // Members can quote/edit messages; shared-resource viewers (anonymous + logged-in non-members)
    // see messages read-only.
    const accessGranted = SharedHelper.accessGranted(loggedUser, projectId)
    // AT-2449 — Swipeable can deliver its close callbacks the wrong way round
    // and leave the message permanently uneditable. See the hook.
    const { blockOpen, ...swipeCloseGuard } = useSwipeCloseGuard()
    const [panColor, setPanColor] = useState(new Animated.Value(0))
    const itemSwipe = useRef(null)
    const dismissibleRef = useRef(null)
    const editOpenTimeoutRef = useRef(null)
    const creatorData = useGetUserPresentationData(message.creatorId, { fromAssistant: message.fromAssistant })
    const showFloatPopup = useSelector(state => state.showFloatPopup)
    const activeChatMessageId = useSelector(state => state.activeChatMessageId)

    const enableEditMode = () => {
        const hasDismissibleRef = !!dismissibleRef.current
        const modalVisibleBefore = hasDismissibleRef ? dismissibleRef.current.modalIsVisible() : null

        console.log('[ChatEditDebug] enableEditMode called', {
            messageId: message.id,
            blockOpen,
            userIsAnonymous,
            showFloatPopup,
            activeChatMessageId,
            hasDismissibleRef,
            modalVisibleBefore,
        })

        if (!message.pendingSend && !blockOpen && dismissibleRef.current) {
            dispatch(setActiveChatMessageId(message.id))
            if (editOpenTimeoutRef.current) clearTimeout(editOpenTimeoutRef.current)
            editOpenTimeoutRef.current = setTimeout(() => {
                dismissibleRef.current?.openModal(true)
            })

            setTimeout(() => {
                console.log('[ChatEditDebug] enableEditMode after openModal', {
                    messageId: message.id,
                    modalVisibleAfter: dismissibleRef.current?.modalIsVisible?.(),
                })
            })
        } else {
            console.log('[ChatEditDebug] enableEditMode blocked', {
                messageId: message.id,
                blockOpen,
                hasDismissibleRef,
            })
        }
    }

    useEffect(() => {
        return () => {
            if (editOpenTimeoutRef.current) clearTimeout(editOpenTimeoutRef.current)
        }
    }, [])

    const onQuote = () => {
        const { displayName } = creatorData
        dispatch(
            setQuotedText({
                text: message.commentText,
                userName: displayName,
            })
        )
        dismissibleRef.current.closeModal()
    }

    const renderLeftSwipe = (progress, dragX) => {
        // Swipeable calls this during its own render, so setting state synchronously would update
        // this component while another one renders. dragX is stable per Swipeable instance, so
        // deferring the swap adopts it just as reliably, one render later.
        if (panColor !== dragX) {
            Promise.resolve().then(() => setPanColor(dragX))
        }
        return <View style={{ width: 150 }} />
    }

    const onLeftSwipe = () => {
        itemSwipe.current.close()
        onQuote()
    }

    // Expire stale running spinners, but never hide a plan, question, or approval
    // while the VM job is deliberately waiting for the user.
    const effectiveIsLoading = resolveEffectiveMessageLoading(
        message,
        getTimestampInMilliseconds(message.lastChangeDate)
    )

    const isQueued = message.pendingSend?.status === 'queued'

    const backColor = panColor.interpolate({
        inputRange: [-100, 0, 100],
        outputRange: [colors.UtilityYellow125, '#ffffff', colors.UtilityGreen125],
        extrapolate: 'clamp',
    })

    return (
        <View style={{ paddingVertical: 8, marginLeft: 14 }}>
            <SwipeAreasContainer pointerEvents="none" style={{ paddingBottom: 16 }} leftText="Quote" />
            <Swipeable
                useNativeAnimations={false}
                ref={itemSwipe}
                leftThreshold={80}
                enabled={accessGranted && dismissibleRef.current && !dismissibleRef.current.modalIsVisible()}
                renderLeftActions={renderLeftSwipe}
                onSwipeableLeftWillOpen={onLeftSwipe}
                overshootLeft={false}
                overshootRight={false}
                friction={2}
                containerStyle={{ overflow: 'visible' }}
                failOffsetY={[-5, 5]}
                {...swipeCloseGuard}
            >
                <Animated.View
                    style={{
                        backgroundColor: backColor,
                        marginHorizontal: -8,
                        paddingHorizontal: 8,
                        borderRadius: 4,
                    }}
                >
                    {/* AT-2922: a queued message is not sent yet, so it is dimmed.
                        AT-2963: dim the CONTENT, never this row. The row's opaque background is
                        what hides the swipe-to-quote layer underneath it; a translucent row let the
                        green "Quote" area show through and lie over the avatar and the name. */}
                    <View testID="message-item-foreground" style={isQueued ? QUEUED_CONTENT_STYLE : undefined}>
                        <MessageItemHeader
                            projectId={projectId}
                            message={message}
                            serverTime={serverTime}
                            creatorData={creatorData}
                            highlight={highlight}
                            onEditPress={enableEditMode}
                            editDisabled={!accessGranted || !!message.pendingSend}
                            accessGranted={accessGranted}
                            linkedEmailNew={accessGranted && !!linkedEmail && linkedEmailNew}
                        />
                        <MessageItemContent
                            chat={chat}
                            projectId={projectId}
                            commentText={message.commentText}
                            chatTitle={chatTitle}
                            members={members}
                            messageId={message.id}
                            creatorId={message.creatorId}
                            dismissibleRef={dismissibleRef}
                            creatorData={creatorData}
                            objectType={objectType}
                            setAmountOfNewCommentsToHighligth={setAmountOfNewCommentsToHighligth}
                            isLoading={effectiveIsLoading}
                            assistantRun={message.assistantRun}
                            linkedEmail={linkedEmail}
                            linkedEmailGmailData={message.gmailData}
                            linkedEmailArchiving={linkedEmailArchiving}
                            linkedEmailArchived={linkedEmailArchived}
                            onArchiveLinkedEmail={onArchiveLinkedEmail}
                            canArchiveLinkedEmail={accessGranted}
                        />
                    </View>
                </Animated.View>
            </Swipeable>
        </View>
    )
}
